import debounce from 'lodash/debounce'
import { ChangeEvent, useState, useRef, useEffect, useContext } from 'react'
import { kuiperClassNames } from '@opallabs/kuiper'
import { TextArea, DotLoader } from '@opallabs/lapidary'
import { WeekViewGlobalContext } from '../../../weekViewGlobalContext'
import { useWeekViewSettings } from '../../../WeekViewSettingsContext'
import { AddContentPopover } from '../MomentCardFooter/AddContentPopover'
import { styles } from './styles'

type CreateMomentCardProps = {
  onCreateMoment: (
    momentName: string,
    selectedServices?: Array<{ id: string }>
  ) => void
  onExitForm: () => void
}

const MAX_LENGTH = 60
export const errorMessage = `Moment name cannot be longer than ${MAX_LENGTH} characters.`

export const CreateMomentCard: React.FC<CreateMomentCardProps> = ({
  onCreateMoment,
  onExitForm,
}) => {
  const [momentName, setMomentName] = useState('')
  const [error, setError] = useState(false)
  const [showLoader, setShowLoader] = useState(false)
  const popoverOpen = useRef(false)
  const disableSubmit = useRef(false)
  const { setDisableActiveColumn } = useContext(WeekViewGlobalContext)
  const { placeholderColor, collapseContent } = useWeekViewSettings()

  const textArea = useRef<HTMLTextAreaElement>(null)

  const submit = debounce(
    () => {
      if (momentName !== '' && error === false) {
        onCreateMoment(momentName)
        setShowLoader(true)
      }
    },
    1000,
    { leading: true, trailing: false }
  )

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      return submit()
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      onExitForm()
    }
  }

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length > MAX_LENGTH && error === false) {
      setError(true)
    } else if (e.target.value.length <= MAX_LENGTH && error) {
      setError(false)
    }

    setMomentName(e.target.value)
  }

  const onBlur = () => {
    if (popoverOpen.current || disableSubmit.current) return
    if (momentName) return submit()
    onExitForm()
  }

  const onCreateContent = (selectedServices: Array<{ id: string }>) => {
    setShowLoader(true)
    onCreateMoment(momentName, selectedServices)
  }

  const onOpenPopover = () => {
    popoverOpen.current = true
    setDisableActiveColumn(true)
  }

  const onClosePopover = () => {
    setDisableActiveColumn(false)
    popoverOpen.current = false
    // timeout allows clicking outside the moment card
    // to completly resolve before refocusing the textarea
    // so we do not immediately try to save the moment
    // when the popover closes
    setTimeout(() => textArea.current?.focus(), 0)
  }

  const onBlurButton = () => {
    disableSubmit.current = false
  }

  const onFocusButton = () => {
    disableSubmit.current = true
  }

  useEffect(() => textArea.current?.focus(), [])

  return (
    <div css={styles.cardBody(placeholderColor)}>
      <div style={{ position: 'relative' }}>
        {showLoader ? (
          <DotLoader height={44} />
        ) : (
          <TextArea
            ref={textArea}
            error={error ? errorMessage : ''}
            value={momentName}
            name="Create moment"
            onBlur={() => setTimeout(onBlur, 500)}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder="Add Name..."
            errorStyleOverrides={{ marginBlockEnd: 4 }}
            styleOverrides={styles.textarea(placeholderColor)}
          />
        )}
      </div>
      <div
        css={styles.cardFooter}
        className={kuiperClassNames('emptyMomentCard-footer')}
      >
        <AddContentPopover
          collapseButton={collapseContent}
          disableButton={!momentName || error}
          onCreateContent={onCreateContent}
          onBlurButton={onBlurButton}
          onFocusButton={onFocusButton}
          onClosePopover={onClosePopover}
          onOpenPopover={onOpenPopover}
        />
      </div>
    </div>
  )
}
